'use client'

import { Component, type ErrorInfo, type ReactNode } from 'react'

import { env } from '@/lib/env'

import { ErrorBoundaryFallback } from './error-boundary-fallback'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details, especially when dev mode is enabled
    if (env.NEXT_PUBLIC_DEV_MODE) {
      console.error('ErrorBoundary caught an error:', {
        componentStack: errorInfo.componentStack,
        error,
        errorInfo,
        errorMessage: error.message,
        errorStack: error.stack
      })
    } else {
      // In production, log sanitized error info
      console.error('ErrorBoundary caught an error:', {
        errorMessage: error.message,
        errorName: error.name
      })
    }

    this.setState({
      error,
      errorInfo
    })

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo)
  }

  render() {
    if (this.state.error) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI
      return (
        <ErrorBoundaryFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReload={() => {
            this.setState({ error: null, errorInfo: null })
            window.location.reload()
          }}
        />
      )
    }

    return this.props.children
  }
}
